import { SIGNUP_URL } from "./app";
import { media } from "./media";
import { PRIMARY_CTA_HREF } from "./nav";
import { type SectionHead } from "./sections";

export const KNITTING_HERO = {
  eyebrow: "Learn",
  title: "3D knitting, in action",
  lede: "A 3D knitting machine makes a shaped piece straight from one continuous yarn, one row of loops at a time. Nothing is cut from a sheet of fabric, so almost nothing is wasted. Here is how it works, step by step, with a monkey head designed in LOOP.",
} as const;

export type KnitChapter = {
  readonly id: string;
  // First course of the chapter, counted from 0 like the knitmap.
  readonly from: number;
  readonly title: string;
  readonly body: string;
};

export const KNIT_ALONG_HEAD: SectionHead = {
  eyebrow: "Knit along",
  title: "Watch Suzanne knit",
  lede: [
    "Suzanne, Blender's monkey head, 16 cm ear to ear, in LOOP's Fabric view: 29,969 stitches on 145 needles over 682 courses, drawn from the real knit data. Every loop of yarn appears in the order the machine knits it.",
  ],
};

export const KNIT_ALONG = {
  // Rendered offline by scripts/knit-along with the Fabric view's own yarn renderer.
  // The poster as WebP, a third of the JPEG the page's share image stays (not every site reads WebP).
  video: { src: media("suzanne-knit.mp4"), poster: media("suzanne-knit.webp"), width: 1080, height: 1080 },
  data: media("suzanne-knit.json"),
  knitmap: media("suzanne-knitmap.png"),
  // The knitmap's size, for laying out the page before its data has loaded.
  needles: 145,
  courses: 682,
  label: "Suzanne in LOOP's Fabric view, knitted course by course",
  description:
    "Loops of black and orange yarn cover a monkey head in the order a knitting machine makes them: up from the opening under the muzzle, across the face and eyes, over the crown, round the ears and down the back.",
  failed: "The knitmap didn't load. Reload the page to try again.",
  stripLabel: "Knitmap",
  sliderLabel: "Knitmap course",
  chaptersLabel: "Chapters",
  readouts: {
    course: "Course",
    stitches: "Stitches",
    knitting: "Needles knitting",
    holding: "Needles holding",
  },
  chapters: [
    {
      id: "start",
      from: 0,
      title: "Starting knit",
      body: "Knitting starts at the opening under the muzzle, on 37 needles. The black mouth goes in within the first 40 courses: it is knitted, not sewn on.",
    },
    {
      id: "widen",
      from: 63,
      title: "The face widens",
      body: "In five courses the needles in use jump from 40 to 130: new loops are cast onto empty needles at both edges to make the cheeks, and the black nose comes in. The orange ridges are ottoman welts: every fourth course knits extra loops on one bed, so the fabric puffs out.",
    },
    {
      id: "eyes",
      from: 97,
      title: "Eyes and brow",
      body: "The eyes are knitted into the face, not added later: where an eye begins, the black yarn takes the front of the fabric. Short rows, courses that knit only part of the width, round out the cheeks and brow.",
    },
    {
      id: "crown",
      from: 216,
      title: "Over the crown",
      body: "Here a course often knits only a few needles while up to 139 hold their loops. These short rows wrap the fabric over the top of the head, the same trick that turns the heel of a sock.",
    },
    {
      id: "ears",
      from: 376,
      title: "The ears",
      body: "Each ear is a pocket of short rows at one edge of the needle bed: about a dozen needles knit back and forth while the rest of the head waits on its needles.",
    },
    {
      id: "back",
      from: 520,
      title: "The back of the head",
      body: "Wide courses close the back of the head. Then stitches bind off at both edges and the knit narrows.",
    },
    {
      id: "finish",
      from: 596,
      title: "Back to the opening",
      body: "The last courses run down the back of the muzzle on about 35 needles and finish at the opening the knit started from.",
    },
  ] as readonly KnitChapter[],
} as const;

export const KNIT_OFF_MACHINE_HEAD: SectionHead = {
  eyebrow: "Off the machine",
  title: "Off the needles, onto the model",
  lede: [
    "The machine doesn't knit a head that stands up on its own: Suzanne comes out of the needle bed as limp fabric, hanging from the needles, and takes her shape only on the form. LOOP's Fabric view shows both: it simulates the fabric hanging as the machine knits it, then wraps the finished piece onto the model.",
  ],
};

export type OffMachineStep = KnitStep & {
  // The second of the video the step starts at.
  readonly from: number;
};

export const KNIT_OFF_MACHINE = {
  // Rendered offline by scripts/knit-along (record.mjs --bed) from the Fabric view's own simulation
  // of the piece off the needles, with its yarn renderer, as the knit-along video is.
  video: {
    src: media("suzanne-off-machine.mp4"),
    poster: media("suzanne-off-machine.webp"),
    width: 1080,
    height: 1080,
  },
  label: "Suzanne off the machine and onto the model, in LOOP's Fabric view",
  description:
    "Orange and black knitted fabric grows down from a line of needles, course by course, hanging in folds. Once it is finished, the piece hangs as a long limp tube, then wraps round into a monkey head.",
  stepsLabel: "Off the machine",
  steps: [
    {
      id: "knit",
      from: 0,
      title: "Out of the needle bed",
      body: "Each course comes off the needles at the top, and the takedown draws the fabric below it down. Where short rows hold some needles while their neighbours knit on, the fabric bunches into the pockets that become the cheeks, the crown and the ears.",
    },
    {
      id: "off",
      from: 7.6,
      title: "Off the machine",
      body: "With all 682 courses knitted, the needles have let go wale by wale as each one ended. The piece hangs from the last few: a limp tube with the head's shape folded into it.",
    },
    {
      id: "wrap",
      from: 9,
      title: "Onto the model",
      body: "Pulled over the form, the same fabric wraps round into the head: every stitch turns from the way it hung to where it sits on the model.",
    },
  ] as readonly OffMachineStep[],
} as const;

export const KNIT_BASICS_HEAD: SectionHead = {
  eyebrow: "The basics",
  title: "Loops, courses and wales",
  lede: [
    "Knitted fabric is one yarn pulled through itself, loop by loop, row after row. This is Suzanne's, up close in LOOP's Fabric view.",
  ],
};

export type CloseUp = {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly image: { readonly src: string; readonly width: number; readonly height: number; readonly alt: string };
};

const closeUp = (id: string, alt: string) => ({
  src: media(`suzanne-closeup-${id}.webp`),
  width: 960,
  height: 960,
  alt,
});

export const KNIT_BASICS = {
  label: "Suzanne's knit up close",
  views: [
    {
      id: "fabric",
      title: "The fabric",
      body: "Every stitch is a loop of yarn. From the front, each loop shows its two legs as a V; its head is tucked behind the legs of the loop above.",
      image: closeUp(
        "fabric",
        "Close-up of knitted orange yarn: columns of V-shaped loops, with black yarn showing through a few small eyelets.",
      ),
    },
    {
      id: "loop",
      title: "A loop",
      body: "One loop, pulled through the loop below it and holding the one above. Each loop holds the next, which is what lets knit stretch and recover.",
      image: closeUp("loop", "The same close-up in pale grey, with a single loop picked out in orange: one V."),
    },
    {
      id: "course",
      title: "A course",
      body: "A row of loops, made as the yarn travels across the needles: on a machine, usually one pass of the carriage. This is course 51 of Suzanne's 682.",
      image: closeUp(
        "course",
        "The same close-up in pale grey, with one row of loops picked out in orange from edge to edge.",
      ),
    },
    {
      id: "wale",
      title: "A wale",
      body: "A column of loops made on the same needle, each one pulled through the loop before it.",
      image: closeUp(
        "wale",
        "The same close-up in pale grey, with one column of loops picked out in orange from top to bottom.",
      ),
    },
  ] as readonly CloseUp[],
} as const;

export const KNIT_NEEDLES_HEAD: SectionHead = {
  eyebrow: "On the needles",
  title: "How a loop gets onto a needle",
  lede: [
    "A flat knitting machine has two beds of latch needles: each needle is a hook with a small hinged latch. As the carriage passes, every needle in turn pulls a new loop through the one it holds. One pass, one course.",
  ],
};

export type KnitStep = {
  readonly id: string;
  readonly title: string;
  readonly body: string;
};

export const KNIT_NEEDLES = {
  description:
    "3D animation, seen from in front of the needle bed and a little above: five latch needles hold a course of grey loops. A yarn carrier passes over them, and each needle in turn rises through the loop it holds, takes the orange yarn in its hook and drops, pulling the yarn down through the old loop as it slides off over the hook.",
  // what shows until the 3D scene has loaded, and where the browser cannot draw it
  still: media("needle-bed.webp"),
  stepsLabel: "The knitting cycle",
  steps: [
    {
      id: "rise",
      title: "The needle rises",
      body: "It slides up through the loop it holds. The loop slips down the stem, past the latch, and the latch swings open.",
    },
    {
      id: "feed",
      title: "It takes the yarn",
      body: "The carrier passes over the open hook and lays the new yarn in it.",
    },
    {
      id: "latch",
      title: "The latch shuts",
      body: "As the needle drops, the old loop rides back up the stem and flips the latch shut, closing the yarn into the hook.",
    },
    {
      id: "through",
      title: "A new loop, through the old",
      body: "The old loop slides over the shut hook and off the needle, and the hook draws the yarn down through it. The new loop stays on the needle for the next course; the takedown pulls the old one down into the fabric.",
    },
  ] as readonly KnitStep[],
} as const;

export const KNIT_SHAPING_HEAD: SectionHead = {
  eyebrow: "Shaping",
  title: "Short rows",
  lede: [
    "A knitting machine's bed is straight, and it only ever knits rows. Curves come from short rows: courses that stop part of the way across and turn back, while the needles they skip hold their loops.",
  ],
};

export const KNIT_SHORT_ROWS = {
  description:
    "Animation: a swatch knitted row by row, its knitmap on the left and the fabric on the right. After four full rows, pairs of short rows turn back sooner and sooner on the right, so the left side gains rows the right side does not, and the fabric fans round into a curve.",
  stepsLabel: "How short rows shape",
  steps: [
    {
      id: "full",
      title: "Full rows",
      body: "Every needle knits on every pass, and the fabric grows straight.",
    },
    {
      id: "turn",
      title: "A short row",
      body: "The carriage knits part of the way across and turns back. The needles it skipped hold their loops, grey in the knitmap.",
    },
    {
      id: "pile",
      title: "Shorter and shorter",
      body: "Each pair of short rows turns back two needles sooner. The left side gains rows the right side doesn't, so it grows longer.",
    },
    {
      id: "curve",
      title: "The fabric curves",
      body: "A full row joins the two sides again. Off the needles, the longer side bends round the shorter one: a wedge. Wedge after wedge, the fabric turns a corner, the way a sock turns at the heel.",
    },
  ] as readonly KnitStep[],
} as const;

export type ShortRowRegion = {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  // The knitmap rows, counted from 0 like the knitmap; `to` is the first row after them.
  readonly courses: { readonly from: number; readonly to: number };
  readonly image: { readonly src: string; readonly width: number; readonly height: number; readonly alt: string };
  readonly knitmapAlt: string;
};

const shortRowsImage = (id: string, alt: string) => ({
  src: media(`suzanne-shortrows-${id}.webp`),
  width: 900,
  height: 900,
  alt,
});

export const KNIT_SHORT_ROWS_ON_SUZANNE = {
  title: "Suzanne's short rows",
  intro:
    "593 of Suzanne's 682 courses are short rows, and they set how long each wale runs: 386 loops down the middle of the face and over the crown, just 21 or 22 beside the ears. Here are three places they show, each course picked out in turn in two oranges, with its stretch of the knitmap below.",
  regions: [
    {
      id: "crown",
      title: "The crown",
      body: "Courses 312–380 shorten toward the middle of the bed and widen again, twice over, turning back short of the ears. The top of the head gets rows its sides don't, so from the side they fan out over the crown, like the swatch's wedges.",
      courses: { from: 311, to: 380 },
      image: shortRowsImage(
        "crown",
        "Suzanne in the Fabric view, seen from the side, with courses 312 to 380 in two shades of orange: they fan out from beside the ear over the top of the head.",
      ),
      knitmapAlt:
        "Knitmap of courses 312 to 380: runs of stitches narrowing toward the middle of the bed and widening again, twice over, with the needles beside them holding in grey.",
    },
    {
      id: "ear",
      title: "An ear",
      body: "Each ear is a pocket of short rows at the edge of the bed: 24 courses on just 8 to 24 needles, while more than a hundred others hold their loops. Knitting back and forth over so few needles cups the fabric.",
      courses: { from: 398, to: 422 },
      image: shortRowsImage(
        "ear",
        "Close-up of one of Suzanne's ears in the Fabric view, with courses 399 to 422 in two shades of orange filling the cup of the ear.",
      ),
      knitmapAlt:
        "Knitmap of courses 399 to 422: short runs of 8 to 24 stitches at the right edge of the bed, with the needles across the rest of the bed holding in grey.",
    },
    {
      id: "back",
      title: "The back of the head",
      body: "Courses 466–523 knit pockets of short rows at either edge of the bed, then shorten toward the middle and lengthen again. They knit a band that is deepest at the back of the head and rises behind each ear, curving it round.",
      courses: { from: 465, to: 523 },
      image: shortRowsImage(
        "back",
        "Suzanne from behind in the Fabric view, with courses 466 to 523 in two shades of orange: a band across the back of the head, deepest in the middle and rising behind each ear.",
      ),
      knitmapAlt:
        "Knitmap of courses 466 to 523: short runs at either edge of the bed, then runs of stitches that shorten toward the middle and lengthen again, with the other needles holding in grey.",
    },
  ] as readonly ShortRowRegion[],
} as const;

export const KNIT_PIPELINE_HEAD: SectionHead = {
  eyebrow: "Where LOOP comes in",
  title: "From a 3D model to a knit program",
  lede: [
    "Traditionally a specialist knit programmer plans every course in the machine maker's software. LOOP derives it from the 3D model.",
  ],
};

export const KNIT_PIPELINE = [
  {
    id: "model",
    index: "01",
    title: "Model",
    body: "Start from a 3D surface, an OBJ or .3dm file, and paint zones onto it. Suzanne has seven: face, eyes, nose, mouth and ears, each with its own knit structure.",
  },
  {
    id: "courses",
    index: "02",
    title: "Courses and wales",
    body: "LOOP lays courses and wales over the surface at the stitch density of the chosen structure, and decides where each course starts, turns and ends.",
  },
  {
    id: "knitmap",
    index: "03",
    title: "Knitmap",
    body: "The stitches unroll onto the needle bed, every cast-on, short row and bind-off in place: 145 needles by 682 courses for Suzanne.",
  },
  {
    id: "machine",
    index: "04",
    title: "Machine data",
    body: "LOOP writes knit-ready machine data from the knitmap and replays it in the Fabric view, loop by loop, as in the video above. Production teams then validate it through prototyping.",
  },
] as const;

export const KNIT_PIPELINE_LABEL = "From a 3D model to a knit program";

export const KNIT_PROGRAM_HEAD: SectionHead = {
  eyebrow: "Machine data",
  title: "Suzanne's knit program",
  lede: [
    "This is the .DAT file LOOP wrote for Suzanne: the program a Shima Seiki machine knits her from. Each row is one pass of the carriage and each column one needle, every colour an instruction: knit, tuck, transfer. The columns down the sides set the carriage's speed, the yarn carriers and more. Beside it, the same program as text: LOOP's knitcode, course by course, and knitout, the open format for knitting machines. Like any program, it is proved on a prototype before production.",
  ],
};

export const KNIT_PROGRAM = {
  label: "Suzanne's knit program",
  description:
    "Suzanne's .DAT file drawn as a picture: 7,961 rows of coloured cells, one row per pass of the carriage, with the needles across the middle and the option lines down both sides.",
  file: { label: "Download the .DAT", src: media("suzanne.dat"), name: "Suzanne.dat", size: "1.5 MB" },
  // the same file gzipped, for the viewer: 47 KB to the download's 1.5 MB
  packed: media("suzanne.dat.gz"),
  // where the needles and option lines are, and what each colour and setting means
  labels: media("suzanne-dat.json"),
  // the program as text, read back off the DAT, each gzipped for the page
  text: {
    knitcode: {
      tab: "Knitcode",
      label: "Knitcode",
      src: media("suzanne-knitcode.txt"),
      packed: media("suzanne-knitcode.txt.gz"),
      name: "Suzanne-knitcode.txt",
      size: "0.9 MB",
    },
    knitout: {
      tab: "Knitout",
      label: "Knitout",
      src: media("suzanne.k"),
      packed: media("suzanne.k.gz"),
      name: "Suzanne.k",
      size: "2.3 MB",
    },
  },
  textLabel: "Show the program as",
  courseLabel: "Course",
  rowLabel: "DAT row",
  showOnDat: "Show this course on the DAT",
  loadingText: "Loading…",
  overviewLabel: "All",
  hint: "Drag to look around, pinch or Ctrl-scroll to zoom, and hover or tap a cell to read it; the text follows.",
  touchHint: "Move and zoom with two fingers, tap a cell to read it or the strip to jump there; the text follows.",
  downloadsLabel: "Download",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  fit: "Fit the width",
  failed: "The program didn't load. Reload the page to try again.",
} as const;

export const KNITTING_CTA = {
  eyebrow: "Your turn",
  title: "Knit your own shape",
  lede: "Bring a 3D model. LOOP plans every course, from cast-on to bind-off.",
  primary: { label: "Create for free", href: SIGNUP_URL },
  secondary: { label: "See plans", href: PRIMARY_CTA_HREF },
} as const;
